import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '../../../theme/ThemeProvider';
import { logMarketingMaterialsTabViewed } from '../../services/analytics';

export const PROJECT_ESSENTIAL_THEME_MESSAGE = 'transformation-set-project-essential-theme';

export interface MarketingMaterialsTabProps {
  analyticsCea?: string | null;
}

/**
 * Embeds Project Essentials in-place. Same-origin cookies keep the user
 * signed in; theme is mirrored from Super Picker via query param (first
 * paint) + postMessage (live updates when the parent theme changes).
 */
export default function MarketingMaterialsTab({ analyticsCea }: MarketingMaterialsTabProps) {
  const { currentTheme } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Keep src stable so theme toggles don't remount PE; postMessage handles updates.
  const [iframeSrc] = useState(
    () => `/project-essential?embed=1&theme=${encodeURIComponent(currentTheme)}`,
  );

  useEffect(() => {
    logMarketingMaterialsTabViewed(analyticsCea);
  }, [analyticsCea]);

  const postTheme = () => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: PROJECT_ESSENTIAL_THEME_MESSAGE, theme: currentTheme },
      window.location.origin,
    );
  };

  useEffect(() => {
    postTheme();
  }, [currentTheme]);

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'calc(100vh - 140px)', sm: 'calc(100vh - 160px)' },
        minHeight: 480,
        border: '1px solid var(--color-border)',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'var(--color-surface)',
      }}
    >
      <Box
        component="iframe"
        ref={iframeRef}
        title="Project Essentials"
        src={iframeSrc}
        onLoad={postTheme}
        sx={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
        }}
      />
    </Box>
  );
}
